// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
export interface MedplumAppConfig {
  baseUrl?: string;
  googleClientId?: string;
  clientId?: string;
  projectId?: string;
  organizationId?: string;
  recaptchaSiteKey?: string;
  insuranceDeferredBillingEnabled: boolean;
  /** Sends new patients from `/register` into the self-intake v3 flow at `/intake`. */
  intakeV3Enabled: boolean;
  /**
   * Polaris environment this app is running in (dev-<handle>, e2e, preview-<pr>,
   * staging, production). Phase 1 of the environment-isolation plan exposes this
   * value but does not yet use it for routing or data scoping — see
   * docs/ENVIRONMENT_ISOLATION_PLAN.md.
   */
  polarisEnv?: string;
  schedulingEngine: SchedulingEngine;
  stripePublishableKey?: string;
  providerAppBaseUrl?: string;
  patientAppGa4MeasurementId?: string;
}

export type SchedulingEngine = 'legacy' | 'medplum-v2';

export function parseSchedulingEngine(value: string | undefined | null): SchedulingEngine {
  const engine = value?.trim() || 'medplum-v2';
  if (engine === 'medplum-v2') {
    return engine;
  }

  throw new Error(`Invalid VITE_SCHEDULING_ENGINE: ${value}. Only medplum-v2 is supported.`);
}

const config: MedplumAppConfig = {
  baseUrl: import.meta.env?.MEDPLUM_BASE_URL ?? import.meta.env?.VITE_MEDPLUM_BASE_URL,
  googleClientId: import.meta.env?.GOOGLE_CLIENT_ID,
  clientId: import.meta.env?.MEDPLUM_CLIENT_ID,
  projectId: import.meta.env?.MEDPLUM_PROJECT_ID ?? import.meta.env?.VITE_MEDPLUM_PROJECT_ID,
  organizationId: import.meta.env?.VITE_MEDPLUM_ORGANIZATION_ID,
  recaptchaSiteKey: import.meta.env?.VITE_PATIENT_APP_RECAPTCHA_SITE_KEY,
  insuranceDeferredBillingEnabled: import.meta.env?.VITE_PATIENT_APP_INSURANCE_DEFERRED_BILLING_ENABLED === 'true',
  intakeV3Enabled: import.meta.env?.VITE_PATIENT_APP_INTAKE_V3_ENABLED === 'true',
  polarisEnv: import.meta.env?.VITE_POLARIS_ENV,
  schedulingEngine: parseSchedulingEngine(import.meta.env?.VITE_SCHEDULING_ENGINE),
  stripePublishableKey: import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY,
  providerAppBaseUrl: import.meta.env?.VITE_PROVIDER_APP_BASE_URL,
  patientAppGa4MeasurementId: import.meta.env?.VITE_PATIENT_APP_GA4_MEASUREMENT_ID,
};

export function getConfig(): MedplumAppConfig {
  return config;
}
